import { ComponentProps, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Button } from "~/components/button";
import { Icon } from "~/components/icon/icon";
import { Text } from "~/components/text";
import { downloadFromOrgFiles } from "~/integrations/firebase/storage";
import { logError } from "~/shared/services/error-reporting";
import { formatDateTime } from "~/shared/utils";
import { toast } from "~/utils/toast";
import { ManuscriptProcessingReportWithId } from "~/integrations/firebase/collections/manuscript-processing-reporting";
import { reportFileName } from "~/pages/settings/organisations/organisation/manuscript-processing/utils/manuscript-processing-report";
import { ManuscriptProcessingFileCaptionValue } from "./manuscript-processing-file-caption-value";
import { ManuscriptProcessingStatusBadge } from "./manuscript-processing-status-badge";

type Caption = ComponentProps<typeof ManuscriptProcessingFileCaptionValue> & {
  key: string;
};

export const ManuscriptProcessingFile = ({
  report,
  onSelect,
}: {
  report: ManuscriptProcessingReportWithId;
  onSelect: () => void;
}) => {
  const { t } = useTranslation("settings");
  const [isDownloading, setIsDownloading] = useState(false);
  const fileName = reportFileName(report);

  const handleDownload = async () => {
    if (!report.objectPath || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFromOrgFiles(report.objectPath, fileName);
    } catch (e) {
      logError(e as Error);
      toast.error(t("organisation.manuscriptProcessing.downloadError"));
    } finally {
      setIsDownloading(false);
    }
  };

  const available: (Caption | null)[] = [
    report.contentHash
      ? {
          key: "hash",
          display: report.contentHash.slice(0, 8),
          tooltip: t("organisation.manuscriptProcessing.hash.tooltip"),
          tooltipDetail: report.contentHash,
          copyValue: report.contentHash,
          copyLabel: t("organisation.manuscriptProcessing.hash.copy"),
          copiedMessage: t("organisation.manuscriptProcessing.hash.copied"),
        }
      : null,
    report.isbn
      ? {
          key: "isbn",
          display: report.isbn,
          tooltip: t("organisation.manuscriptProcessing.isbn.tooltip"),
          copyValue: report.isbn,
          copyLabel: t("organisation.manuscriptProcessing.isbn.copy"),
          copiedMessage: t("organisation.manuscriptProcessing.isbn.copied"),
        }
      : null,
    report.productTitle
      ? {
          key: "product",
          display: report.productTitle,
          tooltip: t("organisation.manuscriptProcessing.product.tooltip"),
        }
      : null,
    report.workId
      ? {
          key: "workId",
          display: report.workId,
          tooltip: t("organisation.manuscriptProcessing.workId.tooltip"),
          copyValue: report.workId,
          copyLabel: t("organisation.manuscriptProcessing.workId.copy"),
          copiedMessage: t("organisation.manuscriptProcessing.workId.copied"),
        }
      : null,
    report.jobId
      ? {
          key: "jobId",
          display: report.jobId,
          tooltip: t("organisation.manuscriptProcessing.jobId.tooltip"),
          copyValue: report.jobId,
          copyLabel: t("organisation.manuscriptProcessing.jobId.copy"),
          copiedMessage: t("organisation.manuscriptProcessing.jobId.copied"),
        }
      : null,
    report.createdAt
      ? {
          key: "created",
          display: format(report.createdAt, "P"),
          tooltip: t("organisation.manuscriptProcessing.created.tooltip"),
          tooltipDetail: formatDateTime(report.createdAt),
        }
      : null,
  ];
  const captions = available.filter((caption): caption is Caption =>
    Boolean(caption),
  );

  return (
    <li
      aria-label={fileName}
      className="relative flex items-center justify-between rounded-xl border border-secondary-200 p-3 transition-colors duration-150 ease-out hover:border-secondary-300 hover:bg-secondary-25"
    >
      <div className="flex gap-3">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-secondary-200">
          <Icon name="book-open" iconClassName="w-5! h-5!" />
        </div>
        <div className="flex min-w-0 flex-col justify-center">
          <button
            type="button"
            onClick={onSelect}
            // Stretched over the whole card, so it opens the sheet from
            // anywhere while staying a single keyboard target.
            className="w-fit rounded-sm text-left after:absolute after:inset-0 after:content-[''] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
          >
            <Text as="span" variant="label">
              {fileName}
            </Text>
          </button>
          <Text
            as="div"
            variant="caption"
            // The row gap clears the copy buttons' expanded hit areas, which
            // would otherwise overlap across wrapped lines.
            className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-3"
          >
            {captions.map(({ key, ...caption }, index) => (
              <Fragment key={key}>
                {index > 0 && <span>&middot;</span>}
                <ManuscriptProcessingFileCaptionValue {...caption} />
              </Fragment>
            ))}
            {report.updatedAt && (
              <>
                {captions.length > 0 && <span>&middot;</span>}
                <span>
                  {t("organisation.manuscriptProcessing.updated", {
                    date: format(report.updatedAt, "P"),
                  })}
                </span>
              </>
            )}
          </Text>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <ManuscriptProcessingStatusBadge status={report.status} />
        {report.objectPath && (
          <Button
            variant="tertiary"
            size="small"
            icon={isDownloading ? "loader" : "download"}
            iconClassName={isDownloading ? "animate-spin" : undefined}
            label={t("organisation.manuscriptProcessing.download")}
            onClick={handleDownload}
            disabled={isDownloading}
            // Lifted above the stretched card button, like the copy buttons.
            className="relative"
          />
        )}
      </div>
    </li>
  );
};
